import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { colors, frames, useMediaQuery } from "../../../../Styles";
import { PageHeader, NewInput, GroupCard, NewModal, MainContentPaddingTypes, Overlay } from '../../../../Components';
import { ButtonTypes, ListStyle_Types, NewInputHandles } from '../../../../Definitions';
import { RootState, useAppSelector } from '../../../../Store';
import { Graph_QL } from '../../../../Services';
import { useAnimalGroups } from '../../../../Services/Agrisys_GqlDataCaller/AnimalDataCalls/useAnimalGroups';
import { Pages } from '../../../../Navigation/root';
import CardsView from '../../../../Components/BaseComponents/cardsView';
import NewButton, { NewButtonList } from '../../../../Components/BaseComponents/newButton';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { SET_EDIT_MODE } from '../../../../Store/UI/editModeSlice';

export default function AnimalsGroups() {
  const { t } = useTranslation();
  const dispatchEditMode = useDispatch();
  const mobileXs = useMediaQuery(`(max-width: 570px)`);
  const [addGroupModal, setAddGroupModal] = useState<boolean>(false)
  const editMode = useSelector((state: RootState) => state.uiState.editMode.editMode);
  
  const [groupsForDeletion, setGroupsForDeletion] = useState<number[]>([])

  const animalGroupNameInputRef = useRef<NewInputHandles>(null);

  const farm = useAppSelector(({activeData}) => activeData.farm) ?? "";
  const domain = useMemo(() => (Graph_QL.ObjToString({domain: farm})),[farm]);
  const connection = useMemo(() => (Graph_QL.create({domain: farm})),[farm]);;
  
  const { call: getAnimalGroups, data: animalGroupsState, maintainEntries: createAnimalGroups, deleteAnimalGroups } = useAnimalGroups(connection);

  useEffect(() => {
    getAnimalGroups({fetchAmount: 50, ifIncludeAnimalCount: true})
  },[])

  const groupData = useMemo(() => {
    if (animalGroupsState && animalGroupsState[domain]) {
      return animalGroupsState[domain].data
    }
    return []
  },[animalGroupsState, domain])

  const addGroupModalComponent = (
    <NewModal
      modalState={!!addGroupModal}
      overrides={{ 
        mainContentPadding: MainContentPaddingTypes.secondary
      }}
      predefinedButtons={{
        onClose: () => setAddGroupModal(false)
      }}
      header={t("animalPages.animalGroups.newGroup", {defaultValue: "New Group"})}
      mainContent={<NewInput ref={animalGroupNameInputRef} header={t("animalPages.animalGroups.groupName", { defaultValue: "group name" })} />}
      footerContent={<NewButton
        content={t("animalPages.animalGroups.createGroup", {defaultValue: "create group"})}
        buttonType={ButtonTypes.primaryButton}
        overrides={{
          baseStyles: {width: "fit-content"}
        }}
        onClick={() => {
          if (animalGroupNameInputRef.current?.value && createAnimalGroups) {
            createAnimalGroups({data: [{name: animalGroupNameInputRef.current.value}]})
          }
          setAddGroupModal(false);
        }}
      />}
    />
  )
  
  return (
    <frames.Page_inner>
      {editMode ? <Overlay beforeUnload onClickAlert /> : <></>}
      <PageHeader
        zIndex={99}
        topHeader={t(`menuHeaders.${Pages.animals}`, { defaultValue: "animals" })}
        subHeader={t(`menuHeaders.${Pages.animalsGroups}`, { defaultValue: "animal groups" })}
        optionButtons={{
          contents: ["Delete group"],
          onClick: () => { dispatchEditMode(SET_EDIT_MODE(true))}       
        }}
      />

      <frames._page_inner_content $hasScroll>
        <frames._page_inner_content_scroll>  
          <div style={{ zIndex: editMode ? 9999 : 0, position: editMode ? "relative" : "unset" }}>
            <CardsView
              items={groupData.filter((item) => !groupsForDeletion.includes(item.animal_group_id)).map((item, index) => (
                <GroupCard
                  key={index}
                  selectedGroup={item}
                  onClick={editMode ? () => {setGroupsForDeletion((old) => { return [...old, item.animal_group_id] }) } : undefined}
                />
              ))}
              addExtraHeight={mobileXs ? true : false}
              itemsHeight={70}
              cardBtn={{
                content: t("animalPages.animalGroups.createGroup", {defaultValue: "create group"}),
                onCardBtnClick: () => { setAddGroupModal(true) },
                height: "50px",
                disabled: editMode ? true : false
              }}
            />
          </div>
        </frames._page_inner_content_scroll>
      </frames._page_inner_content>
    
      {addGroupModalComponent}
      
      {editMode && createPortal(
        <div style={{ position: "absolute", zIndex: 9999, bottom: "40px", left: "50%", transform: "translateX(-50%)", backgroundColor: colors.greyDarkPrimary, padding: "20px", borderRadius: "10px", boxShadow: "0px 0px 5px 2px" + colors.blackPrimary + "4d", }}>
        <NewButtonList 
          contents={[t("globalSettingsPages.globalSettings.saveChanges", {defaultValue: "save changes"}), t("globalSettingsPages.globalSettings.discardChanges", {defaultValue: "discard changes"})]} 
          onClick={(i) => { 
            if (i === 0) { dispatchEditMode(SET_EDIT_MODE(false)); deleteAnimalGroups(groupsForDeletion); setGroupsForDeletion([]) }
            if (i === 1) { dispatchEditMode(SET_EDIT_MODE(false)); setGroupsForDeletion([]) }
          }}
          overrides={(i) => ({
            baseStyles: { backgroundColor: i === 0 ? colors.blueDarkPrimary : colors.greyDarkPrimary, border: i === 1 ? "3px solid "+colors.blueDarkPrimary : "none"},
            hoverStyles: { backgroundColor: i === 0 ? colors.blueDarkSecondary : colors.blueDarkPrimary },
          })}
          listOverrides={{
            listStyle: mobileXs ? ListStyle_Types.vertical : ListStyle_Types.horizontal
          }} 
        />
      </div>, document.body)
    }
    </frames.Page_inner>
  )
}